import { MoreVertical, Eye, Download, Share2, Pencil, FolderInput, Copy, Star, StarOff, History, Activity, Trash2, RotateCcw, XCircle, ExternalLink, Tag, UploadCloud, Pin } from 'lucide-react';
import Menu, { MenuItem, MenuSeparator } from '../common/Menu';
import { useAuth } from '../../context/AuthContext';
import PermissionGuard from '../common/PermissionGuard';
import { IconButton } from '../common/Button';
import { P } from '../../utils/permissions';

/**
 * The `⋮` menu. Every entry is wrapped in a PermissionGuard that checks both the global
 * permission and the per-resource capability the API returned — an action the user cannot
 * perform is absent, never disabled (UX Rule 3).
 */
export default function DocumentActions({ item, actions, trigger, onOpenChange, align = 'end' }) {
  const { canPin } = useAuth();
  const isFolder = item.type === 'folder';
  const inTrash = Boolean(item.deletedAt);

  if (inTrash) {
    return (
      <Menu
        align={align}
        onOpenChange={onOpenChange}
        trigger={trigger || <IconButton icon={MoreVertical} label={`Actions for ${item.name}`} size={16} />}
      >
        <MenuItem icon={RotateCcw} onClick={() => actions.restore(item)}>
          Restore
        </MenuItem>
        <PermissionGuard permission={P.DOCUMENT_DELETE_PERMANENT}>
          <>
            <MenuSeparator />
            <MenuItem icon={XCircle} destructive onClick={() => actions.purge(item)}>
              Delete permanently
            </MenuItem>
          </>
        </PermissionGuard>
      </Menu>
    );
  }

  return (
    <Menu
      align={align}
      onOpenChange={onOpenChange}
      trigger={trigger || <IconButton icon={MoreVertical} label={`Actions for ${item.name}`} size={16} />}
    >
      <MenuItem icon={Eye} onClick={() => actions.open(item)}>
        {isFolder ? 'Open' : 'Open preview'}
      </MenuItem>

      {!isFolder && (
        <PermissionGuard permission={P.DOCUMENT_DOWNLOAD} resource={item} capability="download">
          <MenuItem icon={Download} onClick={() => actions.download(item)}>
            Download
          </MenuItem>
        </PermissionGuard>
      )}

      <PermissionGuard permission={P.DOCUMENT_SHARE} resource={item} capability="share">
        <MenuItem icon={Share2} onClick={() => actions.share(item)}>
          Share
        </MenuItem>
      </PermissionGuard>

      <PermissionGuard permission={P.DOCUMENT_SHARE_EXTERNAL} resource={item} capability="share" feature="externalSharing">
        {!isFolder && (
          <MenuItem icon={ExternalLink} onClick={() => actions.externalLink(item)}>
            Create secure link
          </MenuItem>
        )}
      </PermissionGuard>

      {/* Shown only to people who can pin somewhere — the server answers that in
          /users/me, since the rule is not expressible as a single permission key. */}
      {!isFolder && canPin && (
        <MenuItem icon={Pin} onClick={() => actions.pin(item)}>
          Pin for others
        </MenuItem>
      )}

      <MenuSeparator />

      <PermissionGuard permission={isFolder ? P.FOLDER_RENAME : P.DOCUMENT_EDIT} resource={item} capability="edit">
        <MenuItem icon={Pencil} onClick={() => actions.rename(item)}>
          Rename
        </MenuItem>
      </PermissionGuard>

      {!isFolder && (
        <PermissionGuard permission={P.DOCUMENT_EDIT} resource={item} capability="edit">
          <MenuItem icon={Tag} onClick={() => actions.editDetails(item)}>
            Edit details
          </MenuItem>
        </PermissionGuard>
      )}

      {!isFolder && (
        <PermissionGuard permission={P.DOCUMENT_REPLACE} resource={item} capability="replace">
          <MenuItem icon={UploadCloud} onClick={() => actions.replace(item)}>
            Upload new version
          </MenuItem>
        </PermissionGuard>
      )}

      <PermissionGuard permission={P.FOLDER_MOVE} resource={item} capability="edit">
        <MenuItem icon={FolderInput} onClick={() => actions.move(item)}>
          Move to…
        </MenuItem>
      </PermissionGuard>

      {!isFolder && (
        <PermissionGuard permission={P.DOCUMENT_UPLOAD} resource={item} capability="download">
          <MenuItem icon={Copy} onClick={() => actions.copy(item)}>
            Make a copy
          </MenuItem>
        </PermissionGuard>
      )}

      <MenuItem icon={item.isFavorite ? StarOff : Star} onClick={() => actions.toggleFavorite(item)}>
        {item.isFavorite ? 'Remove from favourites' : 'Add to favourites'}
      </MenuItem>

      {!isFolder && (
        <>
          <MenuSeparator />
          <MenuItem icon={History} onClick={() => actions.versions(item)}>
            Manage versions
          </MenuItem>
          <MenuItem icon={Activity} onClick={() => actions.activity(item)}>
            View activity
          </MenuItem>
        </>
      )}

      <PermissionGuard
        permission={isFolder ? P.FOLDER_DELETE : P.DOCUMENT_DELETE}
        resource={item}
        capability="delete"
      >
        <>
          <MenuSeparator />
          <MenuItem icon={Trash2} destructive onClick={() => actions.remove(item)}>
            Delete
          </MenuItem>
        </>
      </PermissionGuard>
    </Menu>
  );
}
